import {
    Directive,
    HostListener,
    OnDestroy,
    ElementRef,
    Input,
    HostBinding,
    Renderer2,
    OnInit,
    AfterViewInit
} from '@angular/core';
import { Subscription } from "rxjs";

import { Popover } from '../models/popover.model';
import { HeightWidthService } from '../services/height-width.service';


@Directive(
    {
    selector: '[popover]',
    standalone: false
}
)
export class PopoverDirective implements OnDestroy, OnInit, AfterViewInit {
    public popoverContent: HTMLDivElement;
    public popoverScrollPad: HTMLDivElement;
    public popoverLeader: HTMLDivElement;
    public _hwSubscription: Subscription;
    public scrollOffsetTop: number;              /* main# 'top position' relative to document */
    public scrollWidth: number;                  /* scroll area - width */
    public scrollHeight: number;                 /* scroll area - height */
    public scrollMarginTop: number;              /* scroll area - margin top */
    public popupPosition: any;                   /* div.popoverScrollPad - position (includes h/w) */
    public hoverItemPosition: any;               /* bascially this.el - position */
    public leaderPosition: any;                  /* triangle leading to the popup - position */
    public overlapAmount: number = 2;            /* offset values to make sure the leader touches the hoverItem */
    public count = 0;

    @Input('popover') config: Popover;

    constructor(public renderer: Renderer2,
                public el: ElementRef,
                public heightWidthService: HeightWidthService) { }

    ngOnInit(): void {
        //get #main height/width when window is resized
        this._hwSubscription = this.heightWidthService.getHW().subscribe(hw => {
            this.scrollWidth = hw.scrollHeadWidth;
            this.scrollHeight = hw.scrollHeadHeight;
            this.scrollMarginTop = hw.scrollHeadMarginTop;
            this.scrollOffsetTop = hw.scrollOffsetTop;
        });
    }

    ngAfterViewInit(): void {
        //force call to get stored service HW data
        this.heightWidthService.sendHW(null);
        this.createPopover();
    }

    @HostBinding('class.visible') public visible: boolean;

    @HostListener('mouseenter')
    onMouseEnter(): void {
        this.visible = true;
        this.detectElementInViewport();
    }

    @HostListener('mouseleave', ['$event'])
    onMouseLeave(event: MouseEvent): void {
        this.iconMouseLeaveOverPopup(event);
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {

        //allow HTML anchor to  fire
        if (!(event.target instanceof HTMLAnchorElement)) {
            //stop click event from bubbling to "label for=''" event
            event.preventDefault();
        }

        if (this.visible) {
            this.visible = false;
        } else {
            this.visible = true;
        }
    }

    ngOnDestroy(): void {
        this.renderer.destroy();
        if (this._hwSubscription)
            this._hwSubscription.unsubscribe();
    }

    /**
     * Adds the popover content to the DOM
     */
    createPopover(): void {

        //leader/triangle
        this.popoverLeader = this.renderer.createElement("div");
        this.popoverLeader.className += 'popover-leader';

        //padding around the content (insets scrollbar when scrolling)
        this.popoverScrollPad = this.renderer.createElement("div");
        this.popoverScrollPad.className += 'popover-content-scroll-pad';

        //content 
        this.popoverContent = this.renderer.createElement("div");
        this.popoverContent.innerHTML = this.config.text;
        this.popoverContent.className += 'popover-content';

        //TODO: check if config.position being used?
        //if config has a position set it, otherwise default it to top
        if (this.config.position) {
            this.renderer.addClass(this.el.nativeElement, this.config.position);
        } else {
            //adding 'top left' as a class causes Angular DOM Exception error
            this.renderer.addClass(this.el.nativeElement, 'top');
            this.renderer.addClass(this.el.nativeElement, 'left');
        }

        //add popover container class for styling purposes
        this.renderer.addClass(this.el.nativeElement, 'popover-container');

        //build DOM
        this.renderer.appendChild(this.popoverScrollPad, this.popoverContent);
        this.renderer.appendChild(this.el.nativeElement, this.popoverLeader);
        this.renderer.appendChild(this.el.nativeElement, this.popoverScrollPad);
    }

    /**
     * Determines if the mouse leave event coordinates are outside of the popup
     * @param event - MouseEvent
     */
    iconMouseLeaveOverPopup(event: MouseEvent): void {
        if (event.clientX <= this.popupPosition.left || event.clientX >= this.popupPosition.right || event.clientY <= this.popupPosition.top || event.clientY >= this.popupPosition.bottom)
            this.visible = false;
    }

    /**
     * Main method to check for clipped content at various positions around the hover item
     */
    detectElementInViewport(): void {
        //remove all previous position classes & styles
        this.removeAllPositionClasses();

        //set position info
        this.hoverItemPosition = this.el.nativeElement.getBoundingClientRect();
        this.popupPosition = this.popoverScrollPad.getBoundingClientRect();
        this.leaderPosition = this.popoverLeader.getBoundingClientRect();

        //Add bottom/top classes
        this.addTopBottomClasses();

        //Add left/right classes
        this.addLeftRightClasses()

        //Add center classes (if necessary)
        this.addCenterClasses();

        //Add max height (if necessary)
        this.addMaxHeight();

        //position the popup
        this.setPopupLocation();
    }

    /**
     * Sets top/bottom classes
     *  Potentially overridden by 'addMaxHeight' method
     */
    addTopBottomClasses(): void {
        if (this.config.position != null && this.config.position.length > 0 && (this.config.position === 'top' || this.config.position === 'bottom')) {
            this.renderer.addClass(this.el.nativeElement, this.config.position);
        }
        else {
            if (this.clippedTop()) {
                this.renderer.addClass(this.el.nativeElement, 'bottom');
            } else {
                //set default top (+ left)
                this.renderer.addClass(this.el.nativeElement, 'top');
            }
            if (this.clippedBottom()) {
                this.renderer.addClass(this.el.nativeElement, 'top');
            }
        }       
    }

    /**
     * Sets left/right classes
     *  Potentially overridden by 'addCenterClasses' method
     */
    addLeftRightClasses(): void {
        if (this.config.position != null && this.config.position.length > 0 && (this.config.position === 'left' || this.config.position === 'right')) {
            this.renderer.addClass(this.el.nativeElement, this.config.position);
        }
        else {
            if (this.clippedLeft()) {
                this.renderer.addClass(this.el.nativeElement, 'right');
            } else {
                //default left (+ top)
                this.renderer.addClass(this.el.nativeElement, 'left');
            }
            if (this.clippedRight()) {
                this.renderer.addClass(this.el.nativeElement, 'left');
            }
        }       
    }

    /**
     * Adds 'center' class
     *  Will remove 'right' & 'left' since they are unecessary if we are centering
     */
    addCenterClasses(): void {
        //do we have a popup that won't fit left or right?
        if (this.clippedLeft() && this.clippedRight()) {

            //remove left & right classes added above
            this.renderer.removeClass(this.el.nativeElement, 'right');
            this.renderer.removeClass(this.el.nativeElement, 'left');

            //add center class
            this.renderer.addClass(this.el.nativeElement, 'center');
        }
    }

    /**
     * Adds max-height to any popup content that is too tall for a given scroll area
     *  Will remove previously added 'top' & 'bottom' classes because they are recalculated in this method
     */
    addMaxHeight(): void {
        //do we have a popup that's too tall?
        if (this.clippedTop() && this.clippedBottom()) {

            let topOrBottom: string;    /* top or bottom has more space for clipped top & bottom */
            let topOrBottomMargin = 5;  /* popover doesn't go to scrolling top/bottom edge */
            let scrollVerticalCenter = (this.scrollHeight - (this.scrollMarginTop + topOrBottomMargin)) / 2; /* middle of scrolling area */

            //remove top & bottom classes added above
            this.renderer.removeClass(this.el.nativeElement, 'bottom');
            this.renderer.removeClass(this.el.nativeElement, 'top');

            //get the vertical center of the icon
            let iconVerticalCenter = (this.hoverItemPosition.bottom + this.hoverItemPosition.top) / 2;

            //figure out if we have more space on top or below
            topOrBottom = (iconVerticalCenter - this.scrollOffsetTop) > scrollVerticalCenter ? 'top' : 'bottom';

            //set top or bottom for maximum available height
            this.renderer.addClass(this.el.nativeElement, topOrBottom);

            //set max-height to little under 1/2 view height 
            this.renderer.setStyle(this.popoverContent, 'max-height', (scrollVerticalCenter - (this.hoverItemPosition.height + topOrBottomMargin)) + "px");
        }
    }

    /**
     * Clipping helpers
     */
    clippedTop(): boolean {
        return this.hoverItemPosition.top - this.popupPosition.height - this.leaderPosition.height  < this.scrollOffsetTop;
    }

    clippedLeft(): boolean {
        return (this.hoverItemPosition.left + (this.hoverItemPosition.width / 2)) - this.popupPosition.width  < 0
    }

    clippedRight(): boolean {
        return (this.hoverItemPosition.left + (this.hoverItemPosition.width / 2)) + this.popupPosition.width > this.scrollWidth;
    }

    clippedBottom(): boolean {
        return this.hoverItemPosition.bottom + this.popupPosition.height + this.leaderPosition.height > this.scrollHeight + this.scrollOffsetTop;
    }

    /**
     * Position the popup in the UI
     */
    setPopupLocation(): void {

        if (this.el.nativeElement.classList.contains("top")) {
            this.setLeaderLocation("top");
            this.setPopupScrollLocation("top");
        }

        if (this.el.nativeElement.classList.contains("bottom")) {
            this.setLeaderLocation("bottom");
            this.setPopupScrollLocation("bottom");
        }

        if (this.el.nativeElement.classList.contains("left")) {
            this.setPopupScrollLocation("left");
        }

        if (this.el.nativeElement.classList.contains("right")) {
            this.setPopupScrollLocation("right");
        }

        if (this.el.nativeElement.classList.contains("center")) {
            this.setPopupScrollLocation("center");
        }

        //set final popover location (for mouseleave)
        setTimeout(() => {
            this.popupPosition = this.popoverScrollPad.getBoundingClientRect();
        }, 0)
    }

    /**
     * Set main popup body location
     * @param direction
     */
    setPopupScrollLocation(direction: any): void {

        switch (direction) {
            case "top":
                this.renderer.setStyle(this.popoverScrollPad, 'top', -(this.leaderPosition.height - this.overlapAmount) + "px");
                break;
            case "bottom":
                this.renderer.setStyle(this.popoverScrollPad, 'top', (this.hoverItemPosition.height + this.leaderPosition.height - this.overlapAmount) + "px");
                break;
            case "left":
                this.renderer.setStyle(this.popoverScrollPad, 'left', (this.hoverItemPosition.width / 2 + this.leaderPosition.width) + "px");
                break;
            case "right":
                this.renderer.setStyle(this.popoverScrollPad, 'left', (this.hoverItemPosition.width / 2 - this.leaderPosition.width) + "px");
                break;
            case "center":
                this.renderer.setStyle(this.popoverScrollPad, 'left', this.hoverItemPosition.width / 2 + "px");
                break;
            default:
                console.log("Missed case in setPopupScrollLocation, check for new direction " + direction);
        }
    }

    /**
     * Set popup triangle location
     * @param direction
     */
    setLeaderLocation(direction: string): void {

        switch (direction) {
            case "top":
                this.renderer.setStyle(this.popoverLeader, 'top', -(this.leaderPosition.height - this.overlapAmount) + "px");
                break;
            case "bottom":
                this.renderer.setStyle(this.popoverLeader, 'top', this.hoverItemPosition.height - this.overlapAmount + "px");
                break;
            default:
                console.log("missed case in setLeaderLocation, check for new leader direction: " + direction);
        }
        //set the left
        this.renderer.setStyle(this.popoverLeader, 'left', (this.hoverItemPosition.width / 2) - (this.leaderPosition.width / 2) + "px");
    }

    /**
     * Clears out all positioning
     */
    removeAllPositionClasses(): void {

        //native element
        this.renderer.removeClass(this.el.nativeElement, 'top');
        this.renderer.removeClass(this.el.nativeElement, 'bottom');
        this.renderer.removeClass(this.el.nativeElement, 'left');
        this.renderer.removeClass(this.el.nativeElement, 'right');
        this.renderer.removeClass(this.el.nativeElement, 'center');

        //popover content
        this.renderer.removeStyle(this.popoverContent, 'max-height');

        //leader
        this.renderer.removeStyle(this.popoverLeader, 'top');
        this.renderer.removeStyle(this.popoverLeader, 'left');

        //popup
        this.renderer.removeStyle(this.popoverScrollPad, 'top');
        this.renderer.removeStyle(this.popoverScrollPad, 'left');
    }
}